import { useEffect, useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { Appearance, Stripe, StripeElementsOptions } from '@stripe/stripe-js';

import { PaymentType, PricingPlanSchema } from 'types';
import { api, LoggerService } from 'services';
import { useRouter } from 'hooks';
import { Payment } from 'containers';
import { Loader, PricingPlanCard } from 'components';

export type CreatorPaymentProps = {
  appearance: Appearance;
  stripePromise: Promise<Stripe | null>;
};

const CreatorPayment = ({ appearance, stripePromise }: CreatorPaymentProps): JSX.Element => {
  const router = useRouter<{
    pricingPlanId?: string;
    payment_intent_client_secret: string;
  }>();
  const { pricingPlanId, payment_intent_client_secret: paymentIntentClientSecret } = router.params;

  const [clientSecret, setClientSecret] = useState<string>('');
  const [pricingPlan, setPricingPlan] = useState<PricingPlanSchema | null>(null);

  const options: StripeElementsOptions = {
    // clientSecret is fetched from backend
    // paymentIntentClientSecret is taken from the return_url
    clientSecret: clientSecret || paymentIntentClientSecret,
    appearance
  };

  useEffect(() => {
    if (pricingPlanId) {
      fetchPricingPlan();
    }

    // paymentIntentClientSecret is taken from Stripe return_url after payment is confirmed.
    // If there is a paymentIntentClientSecret, it means that user completed the payment and no need to fetch client secret from backend.
    if (!paymentIntentClientSecret) {
      fetchClientSecret();
    }
  }, [pricingPlanId, paymentIntentClientSecret]);

  const fetchPricingPlan = async () => {
    try {
      const response = await api.requests.ltv.getPricingPlanById(pricingPlanId || '');

      setPricingPlan(response.data);
    } catch (error) {
      LoggerService.log('FETCH_PRICING_PLAN', error);
    }
  };

  const fetchClientSecret = async () => {
    try {
      // get Stripe PaymentIntent client secret from backend
      const response = await api.requests.ltv.preparePayment(pricingPlanId || '');

      setClientSecret(response.data);
    } catch (error) {
      LoggerService.log('FETCH_CLIENT_SECRET', error);
      router.replace('/pricing');
    }
  };

  const renderContent = () => {
    // when clientSecret and pricingPlan is fetched from backend, payment form is ready to be rendered
    if (clientSecret && pricingPlan) {
      return (
        <Elements options={options} stripe={stripePromise}>
          <div className="mb-24">
            <PricingPlanCard {...pricingPlan} />
          </div>
          <Payment.Form
            paymentType={PaymentType.CREATOR}
            price={pricingPlan?.price}
            pricingPlanId={pricingPlanId || ''}
          />
        </Elements>
      );
    }

    // show PaymentComplete component when return_url is passed
    if (paymentIntentClientSecret) {
      return (
        <Elements options={options} stripe={stripePromise}>
          <Payment.Process pricingPlan={pricingPlan} />
        </Elements>
      );
    }

    return <Loader global />;
  };

  return <>{renderContent()}</>;
};

export default CreatorPayment;
